import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CustomerShell } from "@/components/CustomerShell";
import { BankSheet } from "@/pages/Referral";
import { rupee } from "@/lib/constants";
import { Building2, ChevronLeft, Loader2, Landmark } from "lucide-react";

const SC = { Joined: "bg-slate-100 text-slate-600", "Reward earned": "bg-amber-50 text-amber-700", "Reward paid": "bg-emerald-50 text-emerald-700" };

export default function MySociety() {
    const nav = useNavigate();
    const [rows, setRows] = useState(null);
    const [bank, setBank] = useState(undefined);
    const [showBank, setShowBank] = useState(false);
    const load = () => {
        api.get("/society/mine").then((r) => setRows(r.data)).catch(() => setRows([]));
        api.get("/referral/bank").then((r) => setBank(r.data.bank)).catch(() => setBank(null));
    };
    useEffect(() => { load(); }, []);
    return (
        <CustomerShell>
            <div className="rounded-b-3xl bg-navy px-5 pb-6 pt-6 text-white">
                <button aria-label="Back" onClick={() => nav(-1)} className="mb-3 rounded-full bg-white/10 p-2"><ChevronLeft size={18} /></button>
                <h1 className="font-heading text-2xl font-extrabold">My Society</h1>
                <p className="text-sm text-white/60">Residents you enrolled and your rewards</p>
            </div>
            <div className="space-y-5 px-5 py-5">
                {rows === null && <Loader2 className="mx-auto h-6 w-6 animate-spin text-orange" />}
                {rows?.length === 0 && <p className="text-sm text-navy/50" data-testid="my-society-empty">Your phone number isn't linked to a society yet.</p>}
                {rows?.map((s) => <SocietyCard key={s.id} s={s} />)}
                {rows?.length > 0 && (
                    <button data-testid="my-society-bank-btn" onClick={() => setShowBank(true)} className="flex w-full items-center gap-3 rounded-2xl border border-navy/10 bg-white p-4 text-left">
                        <div className="rounded-xl bg-teal/10 p-2.5"><Landmark size={18} className="text-teal" /></div>
                        <div className="flex-1"><h3 className="font-heading text-sm font-bold text-navy">Payout bank</h3>
                            <p className="text-xs text-navy/50" data-testid="my-society-bank">{bank ? `${bank.account_name} · ${bank.account_number_masked}` : "Add bank details in your name to get paid"}</p></div>
                    </button>
                )}
            </div>
            {showBank && <BankSheet current={bank} onClose={() => setShowBank(false)} onSaved={() => { setShowBank(false); load(); }} />}
        </CustomerShell>
    );
}

function SocietyCard({ s }) {
    return (
        <div data-testid={`my-society-${s.code}`}>
            <div className="flex items-center gap-2"><Building2 size={18} className="text-orange" /><h2 className="font-heading text-lg font-bold text-navy">{s.name}</h2></div>
            <p className="text-xs text-navy/50">{s.area_name} · ₹{s.reward_amount} for each resident's first paid booking</p>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <Stat label="Enrolled" value={s.enrolled} testid={`my-society-enrolled-${s.code}`} />
                <Stat label="Reward balance" value={rupee(s.reward_due)} testid={`my-society-due-${s.code}`} />
                <Stat label="Paid to bank" value={rupee(s.reward_paid)} testid={`my-society-paid-${s.code}`} />
            </div>
            <h3 className="mb-2 mt-4 text-sm font-bold text-navy/70">Residents</h3>
            {s.residents.length === 0 && <p className="text-sm text-navy/40">No residents yet. Share the society QR to get started.</p>}
            <div className="space-y-2">
                {s.residents.map((r, i) => (
                    <div key={i} className="flex items-center justify-between rounded-2xl border border-navy/10 bg-white px-4 py-3" data-testid={`my-society-resident-${i}`}>
                        <div><p className="text-sm font-bold text-navy">{r.first_name}</p><p className="text-xs text-navy/45">Joined {r.joined_at ? new Date(r.joined_at).toLocaleDateString("en-IN") : "—"}</p></div>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${SC[r.status] || "bg-slate-100 text-slate-600"}`}>{r.status}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Stat({ label, value, testid }) {
    return <div className="rounded-2xl border border-navy/10 bg-white p-3"><p className="text-[11px] text-navy/45">{label}</p><p className="font-heading text-base font-extrabold text-navy" data-testid={testid}>{value}</p></div>;
}
